import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import "../../styles/Form.css";
import logo from "../../images/logo.png";

const PspDisclosurePage = () => {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const [username, setUsername] = useState("");
  const loggedInUserId = localStorage.getItem("userId");
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const [isSigned, setIsSigned] = useState(false);
  const [signedDate, setSignedDate] = useState("");
  const effectiveUserId = isAdmin && paramId ? paramId : loggedInUserId;
  const location = useLocation();
  const isSlidePage = location.pathname.startsWith("/slide/");

  // ✅ make sure this matches your DB: SELECT t_id FROM nwt_tasks WHERE t_key='psp_disclosure';
  const TASK_ID = 30;
  const NAME_KEY = "psp_disclosure";

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const token = localStorage.getItem("token");
        const url = isAdmin
          ? `${API_BASE}/api/users/${effectiveUserId}`
          : `${API_BASE}/api/user/profile`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { first_name, last_name } = res.data;
        setUsername(`${first_name} ${last_name}`);
      } catch (err) {
        console.error("Error fetching user name:", err);
      }
    };

    const fetchTaskStatus = async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/api/usertasks/status/${effectiveUserId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const idKey = String(TASK_ID);

        const status =
          response.data.status?.[idKey] ??
          response.data.status?.[NAME_KEY] ??
          "not_completed";

        const done = status === "review" || status === "completed";

        if (done) {
          setIsSigned(true);
          const date =
            response.data.signdates?.[idKey] ??
            response.data.signdates?.[NAME_KEY] ??
            null;
          if (date) setSignedDate(date);
        }
      } catch (err) {
        console.error("Error checking task status:", err);
      }
    };

    fetchUserName();
    fetchTaskStatus();
  }, [isAdmin, effectiveUserId, API_BASE]); // ESLint ok

  const handleReturn = () => {
    if (isAdmin && paramId) navigate(`/userhome/${paramId}`);
    else navigate("/userhome");
  };

  return (
    <>
      {/* Top row */}
      <div className="d-flex justify-content-between align-items-center pt-3 px-3">
        {!isSlidePage && (
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Consent to Pre-Employment Screening Program
          </span>
        )}
        <div className="ms-auto">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={isSlidePage ? () => navigate(-1) : handleReturn}
          >
            {isSlidePage ? "Close" : "Return"}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        className={`form-wrapper my-4 ${isSlidePage ? "slide-narrow" : "px-4"}`}
        style={{ fontSize: "15px", lineHeight: "1.6" }}
      >
        {/* Logo/title block for non-slide */}
        {!isSlidePage && (
          <div className="row align-items-center mb-4">
            <div className="col-3 text-start">
              <img
                src={logo}
                alt="Company Logo"
                style={{ width: 150, height: "auto" }}
              />
            </div>
            <div className="col-6 text-center">
              <h2 className="m-0">
                Consent to Pre-Employment Screening Program
              </h2>
            </div>
            <div className="col-3 text-end">
              <strong>New-Way Trucking Ltd</strong>
              <br />
              7725 48 St. SE, Calgary
              <br />
              Alberta, Canada T2C 2V3
            </div>
          </div>
        )}

        {isSlidePage && (
          <div
            className="d-flex justify-content-between align-items-center px-3"
            style={{
              paddingTop: 16,
              paddingBottom: 8,
              borderBottom: "1px solid #ccc",
            }}
          >
            <h4
              className="m-0"
              style={{ fontWeight: 500, color: "#333", fontSize: 18 }}
            >
              Review and Sign Consent – Consent to Pre-Employment Screening
              Program
            </h4>
          </div>
        )}

        {/* ===== Content (restored) ===== */}
        <div className="mt-4">
          <p>
            I understand that the Pre-Employment Screening Program (PSP) is a
            program that provides motor carrier crash and inspection history
            from the U.S. Federal Motor Carrier Safety Administration (FMCSA). I
            authorize New-Way Trucking Ltd to obtain my PSP record for
            employment screening purposes.
          </p>
          <p>
            I understand this record may include the most recent five years of
            crash data and the most recent three years of roadside inspection
            data, including serious safety violations. I also understand that my
            consent is required to obtain this report and that this
            authorization shall remain valid for a single retrieval in
            connection with this application.
          </p>
          <p>
            By signing below, I acknowledge that I have read and understand this
            disclosure and authorization.
          </p>
        </div>

        <hr className="my-5" />

        {/* Basic info */}
        <form>
          <div className="row mb-3 mt-4">
            <div className="col">
              <label className="form-label">Employee Name</label>
              <input
                type="text"
                className="form-control"
                value={username}
                readOnly
              />
            </div>
            <div className="col">
              <label className="form-label">Date</label>
              <input
                type="text"
                className="form-control"
                value={
                  isSigned && signedDate
                    ? new Date(signedDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                readOnly
              />
            </div>
          </div>
        </form>

        {/* Signature */}
        {effectiveUserId && (
          <>
            <SignatureManager
              taskName={NAME_KEY}
              taskId={TASK_ID}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName={NAME_KEY}
              taskId={TASK_ID}
              userId={effectiveUserId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
};

export default PspDisclosurePage;
